import React, { useEffect, useState } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { isEmpty } from 'lodash'
import debounce from 'lodash/debounce'
import ttiPolyfill from 'tti-polyfill'

import { MetricsApi } from 'api'
import { DateRange, getDefaultRange, getDefaultRangeFor } from 'components/DateRangePicker'
import SavedViews from 'components/SavedViews'
import { driftLoadingStateBuilder } from 'components/States'
import { CompanyDetailsContext } from 'context/companyDetails'
import { CurrentUserContext } from 'context/currentUserContext'
import { EnabledIntegrationsContext } from 'context/enabledIntegrations'
import { FilterGroupsByPage, FilterPage, FiltersContext } from 'context/filtersContext'
import { OrgUsersContext } from 'context/orgUsersContext'
import { SalesforceContext } from 'context/salesforceContext'
import { ScrollContext } from 'context/scrollContext'
import { Card, OpenedSidebar, SidebarOpenContext } from 'context/sidebarOpenContext'
import { SortContext } from 'context/sortContext'
import useFilterGroupsByPage from 'hooks/useFilterGroupsByPage'
import useSalesforceAttributeDefs from 'hooks/useSaleforceAttributeDefs'
import { useSessionMetrics } from 'hooks/useSessionMetrics'
import {
  AccountActivitySortValue,
  ActivityEndUser,
  ActivityType,
  CompanyData,
  ContactType,
  InsightOpportunity
} from 'modules/accountActivity/domain'
import CCRMAccountDetailsPageV2 from 'pages/CCRMAccountDetailsPage'
import CCRMActivityFeedPage from 'pages/CCRMActivityFeedPage'
import CCRMContactDetailsPageV2 from 'pages/CCRMContactDetailsPage'
import DealsPage from 'pages/DealsPage'
import MissedAccountsDemoPage from 'pages/MissedAccountsDemoPage'
import InsightCard from 'sections/CCRM/InsightCard'
import { deserializeDateRange, getActivityFiltersFromQueryString } from 'utils/filters'

import '../css/index.css'
import styles from './styles.module.css'

interface Feature {
  featureName: string
  accessLevel: 'READ' | 'WRITE'
}

// see https://github.com/Driftt/traffic-control/blob/c2c32cf/src/utils/access/index.js#L170-L193
export interface TrafficControlAccess {
  hasScopes: (scopes: string | string[]) => boolean
  hasAnyScope: (scopes: string | string[]) => boolean

  hasFeatures: (features: Feature | Feature[]) => boolean
  hasAnyFeature: (features: Feature | Feature[]) => boolean

  hasAnyRole: (roles: string | string[]) => boolean

  hasGates: (gates: string | string[]) => boolean
  hasAllGates: (gates: string | string[]) => boolean

  hasCutover: (cutover: string | string[]) => boolean
  hasLicense: (licenseName: string) => boolean

  isAdmin: () => boolean
  isAgent: () => boolean
  isUser: () => boolean
  isMultiOrg: () => boolean
  isCalendarUser: () => boolean
  isVideoOnlyUser: () => boolean
  isChatUser: () => boolean
  isBot: () => boolean
}

interface PersistedData {
  deselectedActivities: ActivityType[]
  deselectedContactTypes: ContactType[]
  openedRightSidebar: OpenedSidebar
  expandedCard: Card
  sidebarContact: ActivityEndUser | undefined
  accountOwners: { value: number; label: string }[] | undefined

  accountsSort: AccountActivitySortValue
  accountsDescending: boolean
  activitiesSort: AccountActivitySortValue
  activitiesDescending: boolean
  contactsSort: AccountActivitySortValue
  contactsDescending: boolean
  accountActivitySort: AccountActivitySortValue
  accountActivityDescending: boolean
  dealsSort: AccountActivitySortValue
  dealsDescending: boolean
  filterGroupsByPage: FilterGroupsByPage
  activityScrollOffset: number
  accountsScrollOffset: number
  searchQuery: string
  dateRange: DateRange | undefined
  sidebarDeal: InsightOpportunity | undefined
}

const state: PersistedData = {
  deselectedActivities: [],
  deselectedContactTypes: [],
  openedRightSidebar: OpenedSidebar.NONE,
  expandedCard: Card.EXPLAIN_CARD,
  sidebarContact: undefined,
  accountOwners: [],

  accountsSort: AccountActivitySortValue.ENGAGEMENT_SCORE,
  accountsDescending: true,
  activitiesSort: AccountActivitySortValue.MOST_RECENT,
  activitiesDescending: true,
  contactsSort: AccountActivitySortValue.CONTACT_NAME,
  contactsDescending: true,
  accountActivitySort: AccountActivitySortValue.MOST_RECENT,
  accountActivityDescending: true,
  dealsSort: AccountActivitySortValue.DEAL_AMOUNT,
  dealsDescending: true,
  filterGroupsByPage: {
    ACCOUNTS: [[]],
    ACTIVITIES: [[]],
    ACCOUNT_DETAILS: [[]],
    CONTACT_DETAILS: [[]]
  },
  activityScrollOffset: 0,
  accountsScrollOffset: 0,
  searchQuery: '',
  dateRange: getDefaultRange(),
  sidebarDeal: undefined
}

export const buildRoutes = (SavedViews: React.FC<any>) => ({
  currentUser,
  orgUsersById,
  enabledIntegrations,
  match: { url },
  access
}: {
  currentUser: any
  orgUsersById: any
  enabledIntegrations: any
  match: any
  access: TrafficControlAccess
}) => {
  const [searchQuery, setSearchQuery] = useState<string>(state.searchQuery)

  const [dateRange, setDateRange] = useState<DateRange | undefined>(state.dateRange)

  const [openedRightSidebar, setOpenedRightSidebar] = useState<OpenedSidebar>(
    state.openedRightSidebar
  )
  const [expandedCard, setExpandedCard] = useState<Card>(state.expandedCard)
  const [expandedCards, setExpandedCards] = useState<Card[]>([state.expandedCard])

  const [companyData, setCompanyData] = useState<CompanyData>()

  const [sidebarContact, setSidebarContact] = useState<ActivityEndUser | undefined>(
    state.sidebarContact
  )

  const [sidebarDeal, setSidebarDeal] = useState<InsightOpportunity | undefined>(state.sidebarDeal)

  const [accountsSort, setAccountsSort] = useState<AccountActivitySortValue>(state.accountsSort)
  const [accountsDescending, setAccountsDescending] = useState<boolean>(state.accountsDescending)

  const [activitiesSort, setActivitiesSort] = useState<AccountActivitySortValue>(
    state.activitiesSort
  )
  const [activitiesDescending, setActivitiesDescending] = useState<boolean>(
    state.activitiesDescending
  )

  const [contactsSort, setContactsSort] = useState<AccountActivitySortValue>(state.contactsSort)
  const [contactsDescending, setContactsDescending] = useState<boolean>(state.contactsDescending)

  const [accountActivitySort, setAccountActivitySort] = useState<AccountActivitySortValue>(
    state.accountActivitySort
  )
  const [accountActivityDescending, setAccountActivityDescending] = useState<boolean>(
    state.accountActivityDescending
  )

  const [dealsSort, setDealsSort] = useState<AccountActivitySortValue>(state.dealsSort)
  const [dealsDescending, setDealsDescending] = useState<boolean>(state.dealsDescending)

  const {
    filterGroupsByPage,
    handleRemoveFilter,
    handleClearFilterGroups,
    handleChangeFilterGroups
  } = useFilterGroupsByPage(state.filterGroupsByPage)

  const [activityScrollOffset, setActivityScrollOffset] = useState(state.activityScrollOffset)
  const [accountsScrollOffset, setAccountsScrollOffset] = useState(state.accountsScrollOffset)

  useEffect(() => {
    return () => {
      state.openedRightSidebar = openedRightSidebar
      state.expandedCard = expandedCard
      state.sidebarContact = sidebarContact
      state.accountsSort = accountsSort
      state.accountsDescending = accountsDescending
      state.activitiesSort = activitiesSort
      state.activitiesDescending = activitiesDescending
      state.contactsSort = contactsSort
      state.contactsDescending = contactsDescending
      state.accountActivitySort = accountActivitySort
      state.accountActivityDescending = accountActivityDescending
      state.dealsSort = dealsSort
      state.dealsDescending = dealsDescending
      state.filterGroupsByPage = filterGroupsByPage
      state.searchQuery = searchQuery
      state.dateRange = dateRange
    }
  }, [
    openedRightSidebar,
    expandedCard,
    sidebarContact,
    currentUser,
    accountsSort,
    accountsDescending,
    activitiesSort,
    activitiesDescending,
    contactsSort,
    contactsDescending,
    accountActivitySort,
    accountActivityDescending,
    dealsSort,
    dealsDescending,
    filterGroupsByPage,
    searchQuery,
    dateRange
  ])

  const { salesforceAttributeDefs } = useSalesforceAttributeDefs()

  useEffect(() => {
    window.localStorage.removeItem(`prospector.dateRange.${currentUser.id}`)
  }, [dateRange, currentUser])

  const sessionMetrics = useSessionMetrics()

  return (
    <CompanyDetailsContext.Provider value={{ companyData, setCompanyData }}>
      <SidebarOpenContext.Provider
        value={{
          openedRightSidebar,
          setOpenedRightSidebar,
          expandedCard,
          setExpandedCard,
          setSidebarContact,
          sidebarContact,
          sidebarDeal,
          setSidebarDeal,
          expandedCards,
          setExpandedCards
        }}>
        <FiltersContext.Provider
          value={{
            searchQuery,
            setSearchQuery: debounce(setSearchQuery, 200),
            dateRange,
            setDateRange,
            filterGroupsByPage,
            onRemoveFilter: handleRemoveFilter,
            onChangeFilterGroups: handleChangeFilterGroups,
            onClearFilterGroups: handleClearFilterGroups
          }}>
          <SortContext.Provider
            value={{
              accountsSort,
              setAccountsSort,
              accountsDescending,
              setAccountsDescending,

              activitiesSort,
              setActivitiesSort,
              activitiesDescending,
              setActivitiesDescending,

              contactsSort,
              setContactsSort,
              contactsDescending,
              setContactsDescending,

              accountActivitySort,
              setAccountActivitySort,
              accountActivityDescending,
              setAccountActivityDescending,

              dealsSort,
              setDealsSort,
              dealsDescending,
              setDealsDescending
            }}>
            <EnabledIntegrationsContext.Provider value={enabledIntegrations}>
              <OrgUsersContext.Provider value={orgUsersById}>
                <CurrentUserContext.Provider value={currentUser}>
                  <SalesforceContext.Provider value={salesforceAttributeDefs}>
                    <ScrollContext.Provider
                      value={{
                        accountsScrollOffset,
                        setAccountsScrollOffset: offset => {
                          setAccountsScrollOffset(offset)
                          state.accountsScrollOffset = offset
                        },
                        activityScrollOffset,
                        setActivityScrollOffset: offset => {
                          setActivityScrollOffset(offset)
                          state.activityScrollOffset = offset
                        }
                      }}>
                      <Switch>
                        <Route path={`${url}`} exact>
                          <CCRMActivityFeedPage sessionMetrics={sessionMetrics} />
                        </Route>
                        <Route path={`${url}/insights/:insightId`}>
                          <InsightCard sessionMetrics={sessionMetrics} />
                        </Route>
                        <Route path={`${url}/accounts/:id`} exact>
                          <CCRMAccountDetailsPageV2 sessionMetrics={sessionMetrics} />
                        </Route>
                        <Route path={`${url}/contacts/:id`} exact>
                          <CCRMContactDetailsPageV2 sessionMetrics={sessionMetrics} />
                        </Route>
                        <Route path={`${url}/engagement`}>
                          <SavedViews
                            access={access}
                            filterPage={FilterPage.ACCOUNTS}
                            isExternalPage={true}
                          />
                        </Route>
                        <Route path={`${url}/deals`} exact>
                          <DealsPage />
                        </Route>
                        <Route path={`${url}/demo/missed`} exact>
                          <MissedAccountsDemoPage />
                        </Route>
                        <Route render={() => <Redirect to={`${url}`} />} />
                      </Switch>
                    </ScrollContext.Provider>
                  </SalesforceContext.Provider>
                </CurrentUserContext.Provider>
              </OrgUsersContext.Provider>
            </EnabledIntegrationsContext.Provider>
          </SortContext.Provider>
        </FiltersContext.Provider>
      </SidebarOpenContext.Provider>
    </CompanyDetailsContext.Provider>
  )
}

const Routes = buildRoutes(SavedViews)

let ttiMetricSent = false

export const ActivitiesPackageSubRoutes = (props: any) => {
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false)

  useEffect(() => {
    if (!ttiMetricSent) {
      ttiPolyfill.getFirstConsistentlyInteractive().then(value => {
        MetricsApi.post('/monitoring/metrics/add', {
          value,
          metric: 'prospector-time-to-interactive',
          dimensions: {
            type: 'bucket',
            buckets: '950,1900,3800,4750,5700'
          }
        })

        ttiMetricSent = true
      })
    }
  }, [])

  useEffect(() => {
    if (props.currentUser) {
      const qsActivities = getActivityFiltersFromQueryString()
      if (qsActivities) {
        state.filterGroupsByPage.ACTIVITIES = qsActivities
      }

      const savedDateRangeJson = window.localStorage.getItem(
        `prospector.dateRange.${props.currentUser.id}`
      )
      if (savedDateRangeJson) {
        const savedDateRange = deserializeDateRange(savedDateRangeJson)
        state.dateRange = getDefaultRangeFor(savedDateRange, savedDateRange.type)
      }

      setFiltersLoaded(true)
    }
  }, [props.currentUser])

  if (!filtersLoaded || isEmpty(props.currentUser?.settings)) {
    const Loading = driftLoadingStateBuilder('Loading...')

    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    )
  }

  return <Routes {...props} />
}
